import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  Auth,
  browserLocalPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from '@angular/fire/auth';
import { Router } from '@angular/router';

type Mode = 'login' | 'reset';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  mode: Mode = 'login';

  loginForm: FormGroup;
  resetForm: FormGroup;

  errorMessage = '';
  resetInfoMessage = '';
  resetErrorMessage = '';
  resetSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private auth: Auth,
    private router: Router,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
    this.resetForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit() {
    // De loginGuard stuurt al ingelogde gebruikers weg van deze pagina.
    console.log('Current auth state:', {
      isAuthenticated: !!this.auth.currentUser,
      user: this.auth.currentUser ? {
        uid: this.auth.currentUser.uid,
        email: this.auth.currentUser.email,
        emailVerified: this.auth.currentUser.emailVerified,
      } : null,
    });
  }

  async setAuthPersistence() {
    try {
      await setPersistence(this.auth, browserLocalPersistence);
    } catch (error) {
      console.error('Error setting persistence:', error);
    }
  }

  showForgotPassword() {
    // Prefill met wat er in het login-veld staat, scheelt intypen.
    const loginEmail = (this.loginForm.value.email || '').trim();
    if (loginEmail) this.resetForm.patchValue({ email: loginEmail });
    this.mode = 'reset';
    this.errorMessage = '';
    this.resetInfoMessage = '';
    this.resetErrorMessage = '';
  }

  showLogin() {
    this.mode = 'login';
    this.resetErrorMessage = '';
    this.resetInfoMessage = '';
    this.resetSubmitting = false;
  }

  async onSubmit() {
    if (!this.loginForm.valid) return;
    try {
      const { email, password } = this.loginForm.value;
      await signInWithEmailAndPassword(this.auth, email, password);
      this.router.navigate(['/webshop']);
    } catch (error) {
      console.error('Login error:', error);
      this.errorMessage = 'Ongeldig e-mailadres of wachtwoord';
    }
  }

  async onResetSubmit() {
    if (!this.resetForm.valid || this.resetSubmitting) return;

    this.resetSubmitting = true;
    this.resetErrorMessage = '';
    this.resetInfoMessage = '';

    const email = (this.resetForm.value.email || '').trim();

    try {
      await sendPasswordResetEmail(this.auth, email);
      // Neutrale bevestiging: we onthullen niet of het adres bestaat, dat
      // scheelt account-enumeratie.
      this.resetInfoMessage =
        'Als er een account is met dit e-mailadres, hebben we een reset-link gestuurd. Check je inbox (en de spam-folder).';
      this.resetForm.reset();
    } catch (error: unknown) {
      // Zelfde neutrale melding tonen voor "user-not-found" en
      // "invalid-email"; alleen echte fouten (netwerk, throttling) laten
      // we duidelijk zien.
      const code = (error as { code?: string })?.code || '';
      console.error('Password reset error:', error);
      if (code === 'auth/too-many-requests') {
        this.resetErrorMessage =
          'Te veel pogingen. Wacht een paar minuten en probeer het opnieuw.';
      } else if (code === 'auth/network-request-failed') {
        this.resetErrorMessage =
          'Geen verbinding met de server. Check je internet en probeer opnieuw.';
      } else {
        this.resetInfoMessage =
          'Als er een account is met dit e-mailadres, hebben we een reset-link gestuurd. Check je inbox (en de spam-folder).';
        this.resetForm.reset();
      }
    } finally {
      this.resetSubmitting = false;
    }
  }
}
